<vs-dialog #createEmailTemplateDialog [config]="dialogConfig ?? { dialogContainerClass: 'max-w-[700px]' }">
  <vs-dialog-header
    [config]="{
      mode: 'simple',
      hideCloseButton: false,
      icon: 'plus-circle',
      iconTheme: 'primary',
      showDivider: true,
      title: 'Create Email Template',
      subtitle: 'Configure delivery and template content'
    }"
  ></vs-dialog-header>

  <form [formGroup]="form" class="mt-3">
    <vs-form-field theme="primary" [config]="{ label: 'Delivery Method' }">
      <vs-select vsInput formControlName="deliveryMode">
        <li *ngFor="let mode of deliveryModes" vs-select-option [value]="mode.value">{{ mode.label }}</li>
      </vs-select>
    </vs-form-field>

    <vs-form-field theme="primary" [config]="{ label: 'Template Label' }" class="mt-4">
      <input vsInput type="text" formControlName="templateLabel" placeholder="e.g. Renewal Reminder 45 Days" />
    </vs-form-field>

    <ng-container *ngIf="!isScheduledTemplate">
      <vs-form-field theme="primary" [config]="{ label: 'Event' }" class="mt-4">
        <vs-select vsInput formControlName="type">
          <ng-container *ngFor="let group of eventTypeGroups">
            <li vs-select-option [value]="null" [disabled]="true">
              <span class="text-xs font-semibold uppercase tracking-wide text-gray-500">{{ group.label }}</span>
            </li>

            <li *ngFor="let type of group.options" vs-select-option [value]="type">
              <span class="pl-3">{{ type }}</span>
            </li>
          </ng-container>
        </vs-select>
      </vs-form-field>
    </ng-container>

    <ng-container *ngIf="isScheduledTemplate">
      <vs-form-field theme="primary" [config]="{ label: 'Schedule Anchor' }" class="mt-4">
        <vs-select vsInput formControlName="scheduleAnchor">
          <li *ngFor="let anchor of scheduleAnchors" vs-select-option [value]="anchor.value">{{ anchor.label }}</li>
        </vs-select>
      </vs-form-field>

      <div class="mt-4">
        <label class="text-sm text-gray-700 font-medium">Schedule Offsets (days)</label>
        <p class="text-sm text-gray-600">Add offsets as negative, zero, or positive whole days.</p>
        <div class="mt-2 flex gap-2">
          <input
            vsInput
            type="text"
            [value]="offsetInput"
            (input)="offsetInput = $any($event.target).value"
            placeholder="e.g. -7, 0, +15"
          />
          <button vs-button size="sm" appearance="outline" type="button" (click)="addOffsetFromInput()">Add</button>
        </div>
        <p *ngIf="offsetError" class="text-xs text-red-600 mt-2">{{ offsetError }}</p>
        <div class="mt-2 flex flex-wrap gap-2">
          <span
            *ngFor="let offset of scheduleOffsetsDays"
            class="inline-flex items-center gap-2 rounded-full bg-gray-100 px-3 py-1 text-xs text-gray-700"
          >
            {{ offset > 0 ? '+' + offset : offset }}
            <button type="button" class="text-gray-500 hover:text-gray-900" (click)="removeOffset(offset)">
              &times;
            </button>
          </span>
        </div>
      </div>
    </ng-container>

    <div class="mt-4">
      <label class="text-sm text-gray-700 font-medium">Additional Recipients</label>
      <p class="text-sm text-gray-600">Enter one or more comma-separated email addresses.</p>
      <div class="mt-2 flex gap-2">
        <input
          vsInput
          type="text"
          [value]="recipientInput"
          (input)="recipientInput = $any($event.target).value"
          placeholder="ops@example.com, manager@example.com"
        />
        <button vs-button size="sm" appearance="outline" type="button" (click)="addRecipientsFromInput()">Add</button>
      </div>
      <p *ngIf="recipientError" class="text-xs text-red-600 mt-2">{{ recipientError }}</p>
      <div class="mt-2 flex flex-wrap gap-2">
        <span
          *ngFor="let recipient of additionalRecipients"
          class="inline-flex items-center gap-2 rounded-full bg-gray-100 px-3 py-1 text-xs text-gray-700"
        >
          {{ recipient }}
          <button type="button" class="text-gray-500 hover:text-gray-900" (click)="removeRecipient(recipient)">
            &times;
          </button>
        </span>
      </div>
    </div>

    <vs-form-field theme="primary" [config]="{ label: 'Subject' }" class="mt-4">
      <input vsInput type="text" formControlName="subject" />
    </vs-form-field>

    <vs-form-field theme="primary" [config]="{ label: 'Byline' }" class="mt-4">
      <input vsInput type="text" formControlName="byline" />
    </vs-form-field>

    <label class="text-gray-700 font-medium" style="font-size: 0.875rem; line-height: 2.25rem">Content</label>
    <vs-rich-text-editor formControlName="content"></vs-rich-text-editor>

    <section *ngIf="isWarrantyProvisionedTemplate" class="mt-5 border border-gray-200 rounded-lg p-4">
      <p class="text-sm font-semibold text-gray-700">Attachments</p>
      <p class="text-xs text-gray-500 mt-1">
        Choose the WarrantySphere documents and optional uploaded files that customers will receive.
      </p>

      <div class="mt-4 flex flex-col gap-3">
        <label *ngFor="let option of systemAttachmentOptions" class="flex items-center gap-3 text-sm text-gray-700">
          <input
            vsInput
            type="checkbox"
            [checked]="hasSystemAttachment(option.value)"
            (change)="toggleSystemAttachment(option.value)"
          />
          {{ option.label }}
        </label>
      </div>

      <div class="mt-4">
        <p class="text-sm font-medium text-gray-700 mb-2">Uploaded documents</p>
        <vs-file-input
          formControlName="newAttachments"
          [multiple]="true"
          [moreInfo]="'Multiple files supported. Files upload when you create the template.'"
          [emptyStateText]="'Documents you select will be listed here.'"
        ></vs-file-input>
      </div>

      <p *ngIf="systemAttachments.length === 0 && newAttachments.length === 0" class="text-xs text-gray-500 mt-3">
        No attachments selected.
      </p>
    </section>
  </form>

  <div class="flex footer mt-5 gap-2 justify-end">
    <button vs-button size="md" appearance="outline" [disabled]="saving" (click)="cancel()">Cancel</button>
    <button vs-button size="md" theme="primary" [disabled]="saving || form.invalid" (click)="submit()">Create</button>
  </div>
</vs-dialog>
